import React, { useMemo, useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon, DocumentIcon } from '@heroicons/react/24/solid';
import { EllipsisHorizontalCircleIcon } from '@heroicons/react/24/outline';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

// ##### Segmented In/Out control (same as Flow BoardDynamicTable) #####
const SegmentedFlowType = ({ selectedFlowType, setSelectedFlowType }) => (
  <div className="bg-gray-100 rounded-full p-0.5 inline-flex">
    {['In', 'Out'].map((type, index) => (
      <button
        key={type}
        onClick={() => setSelectedFlowType(type)}
        className={`
          px-3 py-1 text-xs font-medium transition-all
          ${index === 0 ? 'rounded-l-full' : 'rounded-r-full'}
          ${selectedFlowType === type
            ? type === 'In'
              ? 'text-white bg-green-400'
              : 'text-white bg-red-400'
            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
          }
        `}
      >
        {type}
      </button>
    ))}
  </div>
);

// ##### CardBudgetDynamicTable: same content/shape as Flow board dynamic table #####
const CardBudgetDynamicTable = ({
  flows = [],
  budgets = [],
  loading = false,
  selectedFlowCategL0 = '',
  selectedFlowType = 'Out',
  setSelectedFlowType,
  onFlowClick,
}) => {
  const formatPct = (flowValue, budgetValue) => {
    const f = Number(flowValue) || 0;
    const b = Number(budgetValue) || 0;
    if (b === 0) return f > 0 ? '∞' : '0%';
    return `${((f / b) * 100).toFixed(0)}%`;
  };

  const formatDiff = (flowValue, budgetValue) => {
    const f = Number(flowValue) || 0;
    const b = Number(budgetValue) || 0;
    return (b - f).toFixed(2);
  };

  const data = useMemo(() => {
    // Build FLOW hierarchy: L1 -> L2 -> flows
    const flowL1Map = {};
    (flows || []).forEach((flow) => {
      const l1 = flow.categ_l1 || 'No category';
      const l2 = flow.categ_l2 || 'No subcategory';
      if (!flowL1Map[l1]) flowL1Map[l1] = { l1, flowSum: 0, flowCount: 0, l2Map: {} };
      flowL1Map[l1].flowSum += Math.abs(+flow.std_value || 0);
      flowL1Map[l1].flowCount++;
      if (!flowL1Map[l1].l2Map[l2]) {
        flowL1Map[l1].l2Map[l2] = { l2, flowSum: 0, flowCount: 0, flows: [] };
      }
      flowL1Map[l1].l2Map[l2].flowSum += Math.abs(+flow.std_value || 0);
      flowL1Map[l1].l2Map[l2].flowCount++;
      flowL1Map[l1].l2Map[l2].flows.push(flow);
    });

    // Build BUDGET hierarchy: L1 -> L2 (no transactions)
    const budgetL1Map = {};
    (budgets || []).forEach((b) => {
      const l1 = b.class_l1 || 'No category';
      const l2 = b.class_l2 || 'No subcategory';
      const val = Math.abs(+b.std_amount || 0);
      if (!budgetL1Map[l1]) budgetL1Map[l1] = { l1, budgetSum: 0, l2Map: {} };
      budgetL1Map[l1].budgetSum += val;
      const key = `${l1}__${l2}`;
      budgetL1Map[l1].l2Map[key] = (budgetL1Map[l1].l2Map[key] || 0) + val;
    });

    const allL1 = new Set([...Object.keys(flowL1Map), ...Object.keys(budgetL1Map)]);
    const merged = Array.from(allL1).map((l1Key) => {
      const flowL1 = flowL1Map[l1Key];
      const budgetL1 = budgetL1Map[l1Key];

      const flowSum = flowL1?.flowSum || 0;
      const flowCount = flowL1?.flowCount || 0;
      const budgetSum = budgetL1?.budgetSum || 0;

      const flowL2Keys = flowL1 ? Object.keys(flowL1.l2Map) : [];
      const budgetL2Keys = budgetL1 ? Object.keys(budgetL1.l2Map) : [];
      const allL2 = new Set([
        ...flowL2Keys.map((k) => `${l1Key}__${k}`),
        ...budgetL2Keys,
      ]);

      const l2Arr = Array.from(allL2).map((compoundKey) => {
        // compoundKey looks like `${l1}__${l2}`
        const parts = compoundKey.split('__');
        const l2Label = parts.slice(1).join('__') || 'No subcategory';
        const flowL2 = flowL1?.l2Map?.[l2Label];
        const flowL2Sum = flowL2?.flowSum || 0;
        const flowL2Count = flowL2?.flowCount || 0;
        const budgetL2Sum = budgetL1?.l2Map?.[compoundKey] || 0;
        const flowsList = (flowL2?.flows || []).slice().sort((a, b) => Math.abs(b.std_value) - Math.abs(a.std_value));

        return {
          l2: l2Label,
          flowSum: flowL2Sum,
          flowCount: flowL2Count,
          budgetSum: budgetL2Sum,
          flows: flowsList,
        };
      });

      // Sort L2 by max(flow,budget)
      l2Arr.sort((a, b) => Math.max(b.flowSum, b.budgetSum) - Math.max(a.flowSum, a.budgetSum));

      return {
        l1: l1Key,
        flowSum,
        flowCount,
        budgetSum,
        l2Arr,
      };
    });

    // Sort L1 by max(flow,budget)
    merged.sort((a, b) => Math.max(b.flowSum, b.budgetSum) - Math.max(a.flowSum, a.budgetSum));
    return merged;
  }, [flows, budgets]);

  const [expandedL1, setExpandedL1] = useState({});
  const [expandedL2, setExpandedL2] = useState({});
  const toggleL1 = (l1) => setExpandedL1((prev) => ({ ...prev, [l1]: !prev[l1] }));
  const toggleL2 = (l1, l2) =>
    setExpandedL2((prev) => ({ ...prev, [`${l1}__${l2}`]: !prev[`${l1}__${l2}`] }));

  if (loading) {
    return (
      <div className="bg-white shadow-lg rounded-xl p-3 min-h-64 relative overflow-hidden w-full">
        <div className="mb-2">
          <div className="h-5 w-48 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="px-2">
          <table className="min-w-full">
            <tbody>
              {[...Array(6)].map((_, idx) => (
                <tr key={idx}>
                  <td className="py-2">
                    <div className="h-4 w-40 bg-gray-200 rounded animate-pulse mb-1"></div>
                  </td>
                  <td className="py-2">
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse ml-auto"></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
          <SpinnerLoad />
        </div>
      </div>
    );
  }

  if (!(flows || []).length && !(budgets || []).length) {
    return (
      <div className="bg-white shadow-lg rounded-xl p-3 min-h-64 w-full">
        <div className="mb-2 flex justify-between items-center flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-md font-semibold">Detailed info.</span>
            <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5 font-medium">
              {selectedFlowCategL0}
            </span>
          </div>
          {setSelectedFlowType && (
            <SegmentedFlowType
              selectedFlowType={selectedFlowType}
              setSelectedFlowType={setSelectedFlowType}
            />
          )}
        </div>
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <div className="text-gray-400 mb-3">
              <DocumentIcon className="w-16 h-16 mx-auto" />
            </div>
            <div className="text-gray-500 text-sm font-medium mb-2">No flow data to show</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-lg rounded-xl p-3 min-h-64 w-full">
      <div className="mb-2 flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-md font-semibold">Detailed info.</span>
          <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5 font-medium">
            {selectedFlowCategL0}
          </span>
        </div>
        {setSelectedFlowType && (
          <SegmentedFlowType
            selectedFlowType={selectedFlowType}
            setSelectedFlowType={setSelectedFlowType}
          />
        )}
      </div>
      <div>
        <table className="w-full table-fixed border-separate border-spacing-y-1">
          <colgroup>
            <col />
            <col style={{ width: '110px' }} />
            <col style={{ width: '110px' }} />
            <col style={{ width: '110px' }} />
            <col style={{ width: '110px' }} />
          </colgroup>
          <thead>
            <tr>
              <th className="py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-left rounded-l">
                Transaction
              </th>
              <th className="py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                Flow
              </th>
              <th className="py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                Budget
              </th>
              <th className="py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                %
              </th>
              <th className="py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-right rounded-r">
                Diff
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((l1) => (
              <React.Fragment key={l1.l1}>
                <tr
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleL1(l1.l1)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleL1(l1.l1);
                    }
                  }}
                  className="bg-gray-100 hover:bg-gray-200 transition cursor-pointer"
                >
                  <td className="py-1.5 px-2 text-sm font-semibold text-gray-800 rounded-l">
                    <div className="flex items-center min-w-0">
                      <span className="mr-2 text-gray-400 flex-shrink-0">
                        {expandedL1[l1.l1] ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                      </span>
                      <span className="truncate">{l1.l1}</span>
                      <span className="ml-2 text-xs text-gray-400 flex-shrink-0">({l1.flowCount})</span>
                    </div>
                  </td>
                  <td className="py-1.5 px-2 text-sm font-bold text-gray-800 text-right">
                    {l1.flowSum.toFixed(2)}
                  </td>
                  <td className="py-1.5 px-2 text-sm font-bold text-gray-800 text-right">
                    {(l1.budgetSum || 0).toFixed(2)}
                  </td>
                  <td className="py-1.5 px-2 text-sm font-bold text-gray-800 text-right">
                    {formatPct(l1.flowSum, l1.budgetSum)}
                  </td>
                  <td className="py-1.5 px-2 text-sm font-bold text-gray-800 text-right rounded-r">
                    {formatDiff(l1.flowSum, l1.budgetSum)}
                  </td>
                </tr>
                {expandedL1[l1.l1] &&
                  l1.l2Arr.map((l2) => (
                    <React.Fragment key={l2.l2}>
                      <tr
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleL2(l1.l1, l2.l2)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleL2(l1.l1, l2.l2);
                          }
                        }}
                        className="bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
                      >
                        <td className="py-1.5 px-2 text-sm font-normal text-gray-700 pl-4 rounded-l">
                          <div className="flex items-center min-w-0">
                            <span className="mr-2 text-gray-400 flex-shrink-0">
                              {expandedL2[`${l1.l1}__${l2.l2}`] ? (
                                <ChevronDownIcon className="h-4 w-4" />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4" />
                              )}
                            </span>
                            <span className="truncate">{l2.l2}</span>
                            <span className="ml-2 text-xs text-gray-400 flex-shrink-0">({l2.flowCount})</span>
                          </div>
                        </td>
                        <td className="py-1.5 px-2 text-sm font-bold text-gray-800 text-right">
                          {l2.flowSum.toFixed(2)}
                        </td>
                        <td className="py-1.5 px-2 text-sm font-bold text-gray-800 text-right">
                          {(l2.budgetSum || 0).toFixed(2)}
                        </td>
                        <td className="py-1.5 px-2 text-sm font-bold text-gray-800 text-right">
                          {formatPct(l2.flowSum, l2.budgetSum)}
                        </td>
                        <td className="py-1.5 px-2 text-sm font-bold text-gray-800 text-right rounded-r">
                          {formatDiff(l2.flowSum, l2.budgetSum)}
                        </td>
                      </tr>
                      {expandedL2[`${l1.l1}__${l2.l2}`] &&
                        l2.flows.map((flow) => (
                          <tr key={flow.id} className="bg-white hover:bg-gray-50 transition">
                            <td className="py-2 px-2 text-xs text-gray-600 pl-4 rounded-l">
                              <div className="flex items-center gap-2">
                                {onFlowClick && (
                                  <button
                                    onClick={() => onFlowClick(flow)}
                                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-1"
                                    title="Edit transaction"
                                  >
                                    <EllipsisHorizontalCircleIcon className="h-5 w-5" />
                                  </button>
                                )}
                                <div className="flex flex-col flex-1">
                                  <span className="font-medium text-gray-700">{flow.desc}</span>
                                  <span className="text-xs text-gray-400">
                                    {flow.date} | {flow.reference ? flow.reference : '-'}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td
                              className={`py-2 px-2 text-sm text-right font-semibold ${
                                flow.flow_type === 'In' ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {parseFloat(flow.std_value).toFixed(2)}
                            </td>
                            <td className="py-2 px-2 text-sm text-right font-semibold text-gray-300">
                              —
                            </td>
                            <td className="py-2 px-2 text-sm text-right font-semibold text-gray-300">
                              —
                            </td>
                            <td className="py-2 px-2 text-sm text-right font-semibold text-gray-300 rounded-r">
                              —
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CardBudgetDynamicTable;
